import { datadogLogger } from '@telialt/utils/src/datadog/datadogLogger';
import { DDComponent } from '@telialt/utils/src/datadog/setup';

import {
    createAddToCartGAEvent,
    createCheckoutGAEvent,
    createRemoveFromCartGAEvent,
    createSelectItemGAEvent,
    createSelectModifyServiceGAEvent,
    createViewItemGAEvent,
    createViewItemListGAEvent,
} from './lib/core/eventPayloads';
import { pushGTMEvent } from './lib/core/pushGTMEvent';
import type {
    AddToCartInput,
    RemoveFromCartInput,
    SelectItemInput,
    SelectModifyServiceInput,
    ViewItemInput,
    ViewItemListInput,
} from './lib/eventInputs';
import type { CheckoutInput } from './lib/sources/checkout/types';
import {
    createOneTrustGroupsUpdatedEvent,
    createOneTrustLoadedEvent,
    createOptanonLoadedEvent,
} from './lib/sources/consent/consent';
import { createDocumentGAEvent } from './lib/sources/documents/documents';
import type { DocumentEventInput } from './lib/sources/documents/types';
import { createBillPaymentGAEvent, createInvoiceDownloadGAEvent } from './lib/sources/invoices/invoices';
import type { BillPaymentEventInput, InvoiceDownloadEventInput } from './lib/sources/invoices/types';
import { createLeadFormGAEvent } from './lib/sources/leadForms/leadForm';
import type { LeadFormEventInput } from './lib/sources/leadForms/types';
import { createSearchGAEvent, createSearchProductClickGAEvent } from './lib/sources/search/search';
import type { SearchEventInput, SearchProductClickInput } from './lib/sources/search/types';
import { createSubscriptionGAEvent } from './lib/sources/subscription/subscription';
import type { SubscriptionEventInput } from './lib/sources/subscription/subscription';
import { createSurveyGAEvent } from './lib/sources/survey/survey';
import type { SurveyEventInput } from './lib/sources/survey/types';
import type { GTMEventName } from './lib/types';

type TrackOptions = Parameters<typeof pushGTMEvent>[1];
type TrackEventPayload = Parameters<typeof pushGTMEvent>[0];

const trackSafely = (
    input: { source?: string },
    createEvent: () => TrackEventPayload | undefined,
    options?: TrackOptions,
) => {
    let gtmEventName: string | undefined;

    try {
        const event = createEvent();
        gtmEventName = event?.event;

        if (event) {
            pushGTMEvent(event, options);
        }
    } catch (error) {
        try {
            datadogLogger().error(error, {
                component: DDComponent.GTMTracking,
                source: input.source,
                gtmEventName,
            });
        } catch {
            // Ignore logging errors so GTM tracking never breaks the user flow.
        }
    }
};

const GTM_EVENTS = {
    AcceptTermsAndConditions: 'accept_terms_and_conditions',
    AddClientInfo: 'add_client_info',
    AddPaymentInfo: 'add_payment_info',
    AddShippingInfo: 'add_shipping_info',
    AddToCart: 'add_to_cart',
    BeginCheckout: 'begin_checkout',
    CancelPayment: 'cancel_payment',
    CancelSignDocument: 'cancel_sign_document',
    ConfirmPayment: 'confirm_payment',
    DocumentSign: 'document_sign',
    FileDownload: 'file_download',
    Purchase: 'purchase',
    PurchaseEvent: 'purchase_event',
    AutocompleteProductClick: 'autocomplete_product_click',
    AutocompleteSuggestionClick: 'autocomplete_suggestion_click',
    FormOpen: 'formOpen',
    FormStart: 'formStart',
    FormSubmissionSuccess: 'formSubmissionSuccess',
    ProductRecommendationClick: 'product_recommendation_click',
    RemoveFromCart: 'remove_from_cart',
    SearchFormSubmit: 'search_form_submit',
    SearchProductClick: 'search_product_click',
    SearchQuery: 'search_query',
    SearchResultClick: 'search_result_click',
    SearchZeroResults: 'search_zero_results',
    SelectItem: 'select_item',
    SelectModifyService: 'select_modify_service',
    SignDocument: 'sign_document',
    SuccessfulCreditCheck: 'successful_credit_check',
    UnsuccessfulCreditCheck: 'unsuccessful_credit_check',
    ViewItem: 'view_item',
    ViewItemList: 'view_item_list',
} as const satisfies Record<string, GTMEventName>;

export const trackAddToCartGA = (input: AddToCartInput) => {
    trackSafely(input, () => createAddToCartGAEvent(GTM_EVENTS.AddToCart, input));
};

export const trackRemoveFromCartGA = (input: RemoveFromCartInput) => {
    trackSafely(input, () => createRemoveFromCartGAEvent(GTM_EVENTS.RemoveFromCart, input));
};

export const trackViewItemGA = (input: ViewItemInput) => {
    trackSafely(input, () => createViewItemGAEvent(GTM_EVENTS.ViewItem, input));
};

export const trackViewItemListGA = (input: ViewItemListInput) => {
    trackSafely(input, () => createViewItemListGAEvent(GTM_EVENTS.ViewItemList, input));
};

export const trackCheckoutGA = (input: CheckoutInput) => {
    trackSafely(input, () => createCheckoutGAEvent(input));
};

export const trackSelectItemGA = (input: SelectItemInput) => {
    trackSafely(input, () => createSelectItemGAEvent(GTM_EVENTS.SelectItem, input));
};

export const trackSelectModifyServiceGA = (input: SelectModifyServiceInput) => {
    trackSafely(input, () => createSelectModifyServiceGAEvent(GTM_EVENTS.SelectModifyService, input));
};

export const trackSearchGA = (input: SearchEventInput) => {
    trackSafely(input, () => createSearchGAEvent(input));
};

export const trackProductClickGA = (input: SearchProductClickInput) => {
    trackSafely(input, () => createSearchProductClickGAEvent(input));
};

export const trackLeadFormGA = (input: LeadFormEventInput) => {
    trackSafely(input, () => createLeadFormGAEvent(input.data), { resetEcommerce: false });
};

export const trackOneTrustLoaded = (codes: string[]) => {
    trackSafely({ source: 'webviewConsent' }, () => createOneTrustLoadedEvent(codes), { resetEcommerce: false });
};

export const trackOptanonLoaded = (codes: string[]) => {
    trackSafely({ source: 'webviewConsent' }, () => createOptanonLoadedEvent(codes), { resetEcommerce: false });
};

export const trackOneTrustGroupsUpdated = (codes: string[]) => {
    trackSafely({ source: 'webviewConsent' }, () => createOneTrustGroupsUpdatedEvent(codes), {
        resetEcommerce: false,
    });
};

export const trackSurveyGA = (input: SurveyEventInput) => {
    trackSafely(input, () => createSurveyGAEvent(input), { resetEcommerce: false });
};

export const trackSubscriptionGA = (input: SubscriptionEventInput) => {
    trackSafely(input, () => createSubscriptionGAEvent(input));
};

export const trackDocumentGA = (input: DocumentEventInput) => {
    trackSafely(input, () => createDocumentGAEvent(input), { resetEcommerce: false });
};

export const trackInvoiceDownloadGA = (input: InvoiceDownloadEventInput) => {
    trackSafely(input, () => createInvoiceDownloadGAEvent(input), { resetEcommerce: false });
};

export const trackBillPaymentGA = (input: BillPaymentEventInput) => {
    trackSafely(input, () => createBillPaymentGAEvent(input), { resetEcommerce: false });
};
